import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { initPerformanceMonitoring, monitorBundleSize } from './utils/performanceMonitoring';

// Suppress ResizeObserver loop errors - these are harmless warnings from third-party libraries
// (e.g., Google Maps, AdSense) that trigger layout changes during resize observations

// Helper function to check if an error is a ResizeObserver loop error
const isResizeObserverError = (arg) => {
  if (typeof arg === 'string') {
    return arg.includes('ResizeObserver loop') || 
           arg.includes('ResizeObserver loop completed with undelivered notifications');
  }
  if (arg instanceof Error) {
    return arg.message && (
      arg.message.includes('ResizeObserver loop') ||
      arg.message.includes('ResizeObserver loop completed with undelivered notifications')
    );
  }
  if (arg && typeof arg === 'object') {
    const message = arg.message || arg.toString();
    return typeof message === 'string' && (
      message.includes('ResizeObserver loop') ||
      message.includes('ResizeObserver loop completed with undelivered notifications')
    );
  }
  return false;
};

// Override console.error to suppress ResizeObserver warnings
const originalError = window.console.error;
window.console.error = (...args) => {
  // Check all arguments for ResizeObserver errors
  if (args.some(arg => isResizeObserverError(arg))) {
    // Suppress ResizeObserver loop warnings - they're harmless
    return;
  }
  originalError.apply(console, args);
};

// Also override console.warn since React sometimes logs errors as warnings
const originalWarn = window.console.warn;
window.console.warn = (...args) => {
  // Check all arguments for ResizeObserver errors
  if (args.some(arg => isResizeObserverError(arg))) {
    // Suppress ResizeObserver loop warnings - they're harmless
    return;
  }
  originalWarn.apply(console, args);
};

// Also catch ResizeObserver errors at the window level
const originalErrorHandler = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  if (isResizeObserverError(message) || (error && isResizeObserverError(error))) {
    // Suppress ResizeObserver loop warnings - they're harmless
    return true; // Prevent default error handling
  }
  if (originalErrorHandler) {
    return originalErrorHandler(message, source, lineno, colno, error);
  }
  return false;
};

// Handle unhandled promise rejections that might contain ResizeObserver errors
window.addEventListener('unhandledrejection', (event) => {
  if (isResizeObserverError(event.reason) || 
      (event.reason && isResizeObserverError(event.reason.message))) {
    event.preventDefault(); // Suppress the error
    return;
  }
});

// CRITICAL: Intercept React's error overlay handler in development mode
// React's development error overlay listens to error events, so we need to catch them early
// Use capture phase to intercept before React's error overlay
if (process.env.NODE_ENV === 'development') {
  window.addEventListener('error', function(event) {
    if (isResizeObserverError(event.message) || 
        (event.error && isResizeObserverError(event.error))) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  }, true); // Use capture phase to run before other listeners
}

// Override React's reportError API (used by error overlay in development)
// This is the critical fix - React 18+ uses reportError() which bypasses window.onerror
if (typeof window.reportError === 'function') {
  const originalReportError = window.reportError;
  window.reportError = function(error) {
    if (isResizeObserverError(error) || (error && isResizeObserverError(error.message))) {
      // Suppress ResizeObserver errors in reportError
      return;
    }
    return originalReportError.call(this, error);
  };
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Initialize comprehensive performance monitoring
// Start performance monitoring
initPerformanceMonitoring();

// Monitor bundle size after initial load
window.addEventListener('load', () => {
  setTimeout(() => {
    monitorBundleSize();
  }, 1000);
});